import React, { useState, useEffect, useMemo } from 'react';
import { 
  Package, Search, Filter, Layers, 
  CheckCircle, Briefcase, FileSpreadsheet, 
  ChevronLeft, ChevronRight 
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { getStationaryStats, getStationaryInventory } from '../utils/db/bookings';
import { toast } from 'react-toastify';

const StationaryView = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, available: 0, assigned: 0, used: 0 });
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [statsData, inventoryData] = await Promise.all([
        getStationaryStats(user.id),
        getStationaryInventory(user.id)
      ]);
      setStats(statsData);
      setInventory(inventoryData);
    } catch (error) {
      console.error('Failed to load stationary data', error);
      toast.error('Failed to load stationary inventory');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter Data
  const filteredData = useMemo(() => {
    return inventory.filter(item => {
      const matchesSearch = item.awb_no.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            item.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [inventory, searchTerm, statusFilter]);

  // Paginate Data
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Available':
        return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Available</span>;
      case 'Assigned':
        return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700 border border-blue-200">Assigned</span>;
      case 'Used':
        return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-700 border border-slate-200">Used</span>;
      default:
        return <span>{status}</span>;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FileSpreadsheet className="h-6 w-6 mr-2 text-indigo-600" />
            Stationary Inventory
          </h1>
          <p className="text-gray-600 mt-1 text-sm">Monitor your purchased dockets, assignments, and utilization.</p>
        </div>
        <button 
          onClick={loadData}
          className="bg-white border border-slate-200 text-slate-700 hover:bg-yellow-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
        >
          Refresh Data
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Purchased</p>
            <p className="text-3xl font-black text-slate-800 mt-1">{stats.total}</p>
          </div>
          <div className="h-12 w-12 bg-slate-100 rounded-full flex items-center justify-center">
            <Package className="h-6 w-6 text-slate-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-5 flex items-center justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>
          <div>
            <p className="text-sm font-medium text-emerald-600 uppercase tracking-wider">Available</p>
            <p className="text-3xl font-black text-emerald-900 mt-1">{stats.available}</p>
          </div>
          <div className="h-12 w-12 bg-emerald-50 rounded-full flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-emerald-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-blue-100 p-5 flex items-center justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
          <div>
            <p className="text-sm font-medium text-blue-600 uppercase tracking-wider">Assigned</p>
            <p className="text-3xl font-black text-blue-900 mt-1">{stats.assigned}</p>
          </div>
          <div className="h-12 w-12 bg-blue-50 rounded-full flex items-center justify-center">
            <Briefcase className="h-6 w-6 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-indigo-100 p-5 flex items-center justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
          <div>
            <p className="text-sm font-medium text-indigo-600 uppercase tracking-wider">Fully Used</p>
            <p className="text-3xl font-black text-indigo-900 mt-1">{stats.used}</p>
          </div>
          <div className="h-12 w-12 bg-indigo-50 rounded-full flex items-center justify-center">
            <Layers className="h-6 w-6 text-indigo-500" />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
        {/* Controls Bar */}
        <div className="p-4 border-b border-slate-200 bg-yellow-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          
          {/* Search */}
          <div className="relative max-w-sm w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Search AWB or Customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600 font-medium mr-2">Filter Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-300 rounded-lg text-sm py-2 pl-3 pr-8 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="All">All Statuses</option>
              <option value="Available">Available</option>
              <option value="Assigned">Assigned</option>
              <option value="Used">Used</option>
            </select>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">AWB Number</th>
                <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Purchase Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
                      <p>Loading inventory...</p>
                    </div>
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="py-12 text-center text-slate-500">
                    <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-lg font-medium text-slate-600">No dockets found</p>
                    <p className="text-sm">Try adjusting your filters or search term.</p>
                  </td>
                </tr>
              ) : (
                currentData.map((item) => (
                  <tr key={item.id} className="hover:bg-yellow-50 transition-colors group">
                    <td className="py-3 px-6 font-mono text-sm font-semibold text-indigo-900">
                      {item.awb_no}
                    </td>
                    <td className="py-3 px-6">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="py-3 px-6">
                      <div className="text-sm text-slate-900 font-medium">
                        {item.status === 'Available' ? <span className="text-slate-400 italic">Unassigned</span> : item.customer_name}
                      </div>
                      {item.status === 'Used' && (
                        <div className="text-xs text-slate-500 mt-0.5">Receiver: {item.receiver_name}</div>
                      )}
                    </td>
                    <td className="py-3 px-6 text-sm text-slate-600">
                      {new Date(item.purchase_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric'})}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && filteredData.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Showing <span className="font-medium text-slate-900">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium text-slate-900">{Math.min(currentPage * itemsPerPage, filteredData.length)}</span> of <span className="font-medium text-slate-900">{filteredData.length}</span> results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-yellow-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-yellow-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StationaryView;
